import { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useJsonLd } from "@/hooks/useJsonLd";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, isOut, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { GameCover, PlatformBadge } from "@/components/store/GameCover";
import { StockTag } from "@/components/store/GameCard";
import { BulkDeal } from "@/components/store/BulkDeal";

export default function GamePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [game, setGame] = useState(null);
  const [missing, setMissing] = useState(false);
  useSEO(game ? (game.kind === "box" ? {
    title: `${game.name} — $${game.price.toFixed(2)}`,
    description: `${game.codes} random Steam game codes for $${game.price.toFixed(2)}. Every code is worth $5+, some unlock $50–$100 games. Pay with USDT.`,
    path: `/game/${game.id}`,
  } : {
    title: `${game.name} ${game.platform} Key — $${game.price.toFixed(2)}`,
    description: `Buy ${game.name} (${game.platform}) activation key for $${game.price.toFixed(2)}. Pay with USDT on TRC20, ERC20, SOL or BEP20 and get your code by email.`,
    path: `/game/${game.id}`,
    image: game.image_url || undefined,
  }) : { path: `/game/${id}` });

  useEffect(() => { api.get(`/games/${id}`).then((r) => setGame(r.data)).catch(() => setMissing(true)); }, [id]);

  useJsonLd("ld-product", game && {
    "@context": "https://schema.org", "@type": "Product", name: game.name, description: game.description,
    image: game.image_url || "https://gamerealm.org/og-image.jpg", sku: game.id, category: `${game.platform} activation key`,
    brand: { "@type": "Brand", name: game.platform },
    offers: { "@type": "Offer", price: game.price.toFixed(2), priceCurrency: "USD", url: `https://gamerealm.org/game/${game.id}`,
      availability: isOut(game) ? "https://schema.org/OutOfStock" : "https://schema.org/InStock", seller: { "@type": "Organization", name: "GameRealm" } },
  });

  if (missing) return <p data-testid="game-not-found" className="text-center py-32 text-gray-400">This game is no longer available.</p>;
  if (!game) return <div className="h-[60vh]" />;
  const out = isOut(game);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-5 sm:pt-10">
      <Link to="/" data-testid="back-to-store-link" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-3 sm:mb-6 py-2.5"><ArrowLeft size={16} /> Back to store</Link>
      <div className="grid md:grid-cols-2 gap-6 sm:gap-10 items-start">
        <div className="aspect-[460/215] rounded-xl overflow-hidden border border-white/10 rise"><GameCover game={game} /></div>
        <div className="space-y-5 sm:space-y-6 rise" style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-3"><PlatformBadge platform={game.platform} /><StockTag out={out} /></div>
          <h1 data-testid="game-title" className="font-display font-bold tracking-tight text-2xl sm:text-4xl break-words">{game.name}</h1>
          <p className="text-gray-400 text-sm sm:text-base">{game.description}</p>
          <p data-testid="game-price" className="font-mono text-3xl sm:text-4xl font-bold">{money(game.price)} <span className="text-sm text-gray-500 font-normal">USDT</span></p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="buy-now-button"
              disabled={out}
              onClick={() => { add(game); navigate("/checkout"); }}
              className="h-12 px-6 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center justify-center gap-2 hover:bg-emerald-400 disabled:opacity-40 transition-colors"
            ><Zap size={16} /> Buy now</button>
            <button
              data-testid="add-to-cart-button"
              disabled={out}
              onClick={() => { add(game); toast.success("Added to cart"); }}
              className="h-12 px-6 rounded-md border border-white/15 font-semibold inline-flex items-center justify-center gap-2 hover:border-emerald-500/60 disabled:opacity-40 transition-colors"
            ><ShoppingBag size={16} /> Add to cart</button>
          </div>
          <BulkDeal game={game} />
          <div className="rounded-lg border border-white/10 bg-[#0F1117] p-5 text-sm text-gray-400 space-y-2">
            <p><span className="text-white font-semibold">How delivery works:</span> after you send USDT and submit your transaction ID, our team verifies the payment and emails your activation code.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
